/**
 * O acesso pelo QR de uma versão emitida, na tela da equipe (E145.F2, GUS-181, ADR-0099 §2.6).
 *
 * Gera o link da versão (a chave aparece uma vez, na resposta da emissão) e lista os links com a
 * data e o cancelamento. Copiar o link não muda permissão; se a cópia falhar, o link fica
 * selecionável. Só a versão com arquivo do cliente (PDF) tem o que entregar por link.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  emitirAcesso,
  linkDoAcesso,
  listarAcessos,
  revogarAcesso,
  type AcessoPorQrData,
} from '@/lib/acesso_por_qr';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.acessoControlado.equipe;

type Copia = 'parada' | 'copiada' | 'falhou';

export interface AcessoPeloQrProps {
  client: AxiosInstance;
  caseId: string;
  emissaoId: string;
  formato: string | null;
}

export function AcessoPeloQr({ client, caseId, emissaoId, formato }: AcessoPeloQrProps) {
  const [acessos, setAcessos] = useState<AcessoPorQrData[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [copia, setCopia] = useState<Copia>('parada');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (formato !== 'pdf') return undefined;
    listarAcessos(client, caseId, emissaoId)
      .then((lidos) => {
        if (vivo) setAcessos(lidos);
      })
      .catch(() => {
        if (vivo) setErro(true);
      });
    return () => {
      vivo = false;
    };
  }, [client, caseId, emissaoId, formato]);

  if (formato !== 'pdf') {
    return <p className="mt-sc-2 text-caption text-fg-dim">{t.semSaida}</p>;
  }

  async function gerar() {
    setOcupado(true);
    setErro(false);
    setCopia('parada');
    try {
      const emitido = await emitirAcesso(client, caseId, emissaoId);
      setLink(linkDoAcesso(emitido.caminho));
      setAcessos((anteriores) => [...anteriores, emitido.acesso]);
    } catch {
      setErro(true);
    } finally {
      setOcupado(false);
    }
  }

  async function revogar(acessoId: string) {
    setErro(false);
    try {
      const revogado = await revogarAcesso(client, caseId, acessoId);
      setAcessos((lista) => lista.map((a) => (a.id === acessoId ? revogado : a)));
    } catch {
      setErro(true);
    }
  }

  async function copiar(valor: string) {
    try {
      await navigator.clipboard.writeText(valor);
      setCopia('copiada');
    } catch {
      setCopia('falhou');
    }
  }

  return (
    <section className="mt-sc-3" aria-label={t.titulo} data-testid={`acesso-qr-${emissaoId}`}>
      <p className="text-body-sm font-semibold text-fg">{t.titulo}</p>
      <p className="text-caption text-fg-muted">{t.explica}</p>
      <Button type="button" className="mt-sc-2" disabled={ocupado} onClick={() => void gerar()}>
        {ocupado ? t.gerando : t.gerar}
      </Button>
      {link ? <LinkGerado link={link} copia={copia} onCopiar={() => void copiar(link)} /> : null}
      <ListaDeAcessos acessos={acessos} onRevogar={(id) => void revogar(id)} />
      {erro ? (
        <p role="alert" className="mt-sc-2 text-caption text-danger">
          {t.erro}
        </p>
      ) : null}
    </section>
  );
}

function LinkGerado({
  link,
  copia,
  onCopiar,
}: {
  link: string;
  copia: Copia;
  onCopiar: () => void;
}) {
  return (
    <div className="mt-sc-2" data-testid="acesso-qr-link">
      <p role="status" className="text-caption text-fg-muted">
        {t.gerado}
      </p>
      <label className="mt-1 block text-caption text-fg-dim">
        {t.rotuloDoLink}
        <input
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 block w-full border border-border bg-surface px-2 py-1 font-mono text-caption text-fg"
        />
      </label>
      <Button type="button" variant="secondary" className="mt-sc-2" onClick={onCopiar}>
        {t.copiar}
      </Button>
      {copia === 'copiada' ? (
        <p role="status" className="mt-1 text-caption text-fg-muted">
          {t.copiado}
        </p>
      ) : null}
      {copia === 'falhou' ? (
        <p role="alert" className="mt-1 text-caption text-danger">
          {t.copiaFalhou}
        </p>
      ) : null}
    </div>
  );
}

function ListaDeAcessos({
  acessos,
  onRevogar,
}: {
  acessos: AcessoPorQrData[];
  onRevogar: (id: string) => void;
}) {
  if (acessos.length === 0) {
    return <p className="mt-sc-2 text-caption text-fg-dim">{t.nenhum}</p>;
  }
  return (
    <ul className="mt-sc-2 flex flex-col gap-1" aria-label={t.ativos}>
      {acessos.map((acesso) => (
        <li key={acesso.id} className="flex flex-wrap items-center gap-sc-2 text-caption">
          <span className="text-fg-muted">
            {t.criadoEm.replace('{data}', formatarDataHora(acesso.criado_em))}
          </span>
          {acesso.revogado_em ? (
            <span className="text-fg-dim">
              {t.revogadoEm.replace('{data}', formatarDataHora(acesso.revogado_em))}
            </span>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => onRevogar(acesso.id)}>
              {t.revogar}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
